import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatGatewayConfigVersion, gatewayConfigResendEligibleVersion } from '@cnapse/shared/gateway-config-version.js';
import { PRODUCT_NAMES } from '@cnapse/shared';
import { providerGatewayConfigService } from '../../services/ProviderGatewayConfigService';
import {
  OverridesDialog,
  MergedConfigDialog,
} from '../gateway/gateway-config-layer-ui';
import { jsonStringify } from '../../utils/jsonFormat';
import { useGatewayConfigLayerEdits } from '../gateway/gateway-config-layer-state';
import { GatewayConfigPushHistoryTable } from '../gateway/GatewayConfigPushHistoryTable';
import { GatewayConfigLayersTable } from '../gateway/GatewayConfigLayersTable';
import { GatewayConfigPushCommands } from '../gateway/GatewayConfigPushCommands';

function reloadPlanLabel(entry: string): string {
  return entry.replace('orchestrator-hot:', 'hot: ').replace('manager-restart:', 'restart: ');
}

/** Merged API shape (camelCase) → unified overrides shape (kebab-case keys), matching edit dialogs. */
function mergedConfigToUnified(merged: Record<string, unknown>): Record<string, unknown> {
  return {
    orchestrator: merged.orchestrator,
    'barcode-scanner': merged.barcodeScanner,
    'handheld-barcode-scanner': merged.handheldBarcodeScanner,
    'waferid-camera': merged.waferidCamera,
    'efem-adapter': merged.efemAdapter,
  };
}

interface EfemConfigPreviewPayload {
  merged: Record<string, unknown>;
  warnings?: string[];
  reloadPlan?: string[];
  adapterSwap?: {
    previousEfemAdapterKey: string | null;
    newEfemAdapterKey: string;
  } | null;
}

function EfemPreviewExtras({ preview }: { preview: EfemConfigPreviewPayload | null }) {
  if (!preview) return null;
  return (
    <>
      {preview.adapterSwap && (
        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
          Adapter swap: stop {preview.adapterSwap.previousEfemAdapterKey}, start{' '}
          {preview.adapterSwap.newEfemAdapterKey}
        </Alert>
      )}
      {(preview.reloadPlan?.length ?? 0) > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Reload plan:
          </Typography>
          {preview.reloadPlan?.map((entry) => (
            <Chip key={entry} size="small" label={reloadPlanLabel(entry)} variant="outlined" />
          ))}
        </Box>
      )}
    </>
  );
}

interface EfemGatewayConfigPanelProps {
  serialNumber: string;
  tenantId: string | null;
  hasAssignment: boolean;
}

export function EfemGatewayConfigPanel({
  serialNumber,
  tenantId,
  hasAssignment,
}: EfemGatewayConfigPanelProps) {
  const queryClient = useQueryClient();
  const layers = useGatewayConfigLayerEdits();
  const { syncGatewayOverrides, syncTenantOverrides } = layers;
  const [deployPreviewOpen, setDeployPreviewOpen] = useState(false);
  const [deployPreview, setDeployPreview] = useState<EfemConfigPreviewPayload | null>(null);
  const [deployPreviewLoading, setDeployPreviewLoading] = useState(false);
  const [deployPreviewError, setDeployPreviewError] = useState<Error | null>(null);
  const [viewingVersion, setViewingVersion] = useState<string | null>(null);
  const [viewingConfig, setViewingConfig] = useState<EfemConfigPreviewPayload | null>(null);
  const [isLoadingVersionConfig, setIsLoadingVersionConfig] = useState(false);
  const [versionConfigError, setVersionConfigError] = useState<Error | null>(null);

  const gatewayOverridesQuery = useQuery({
    queryKey: ['gateway-config-overrides', serialNumber],
    queryFn: () => providerGatewayConfigService.getGatewayConfigOverrides(serialNumber),
    enabled: !!serialNumber,
  });

  const tenantOverridesQuery = useQuery({
    queryKey: ['tenant-config-overrides', tenantId],
    queryFn: () => providerGatewayConfigService.getTenantConfigOverrides(tenantId!),
    enabled: !!tenantId,
  });

  const historyQuery = useQuery({
    queryKey: ['efem-gateway-config-history', serialNumber],
    queryFn: () => providerGatewayConfigService.getGatewayConfigPushHistory(serialNumber),
    enabled: !!serialNumber,
  });

  const registryTemplateQuery = useQuery({
    queryKey: ['gateway-config-registry-template', serialNumber],
    queryFn: () => providerGatewayConfigService.getGatewayConfigRegistryTemplate(serialNumber),
    enabled: !!serialNumber && hasAssignment,
  });

  useEffect(() => {
    syncGatewayOverrides(gatewayOverridesQuery.data?.data);
  }, [gatewayOverridesQuery.data, syncGatewayOverrides]);

  useEffect(() => {
    syncTenantOverrides(tenantOverridesQuery.data?.data);
  }, [tenantOverridesQuery.data, syncTenantOverrides]);

  const saveGatewayOverridesMutation = useMutation({
    mutationFn: (json: string) => {
      const parsed = JSON.parse(json || '{}') as Record<string, unknown>;
      return providerGatewayConfigService.saveGatewayConfigOverrides(serialNumber, parsed);
    },
    onSuccess: () => {
      layers.markGatewaySaved();
      layers.closeLayerDialog();
      void queryClient.invalidateQueries({ queryKey: ['gateway-config-overrides', serialNumber] });
    },
  });

  const saveTenantOverridesMutation = useMutation({
    mutationFn: (json: string) => {
      if (!tenantId) throw new Error('No tenant selected');
      const parsed = JSON.parse(json || '{}') as Record<string, unknown>;
      return providerGatewayConfigService.saveTenantConfigOverrides(tenantId, parsed);
    },
    onSuccess: () => {
      layers.markTenantSaved();
      layers.closeLayerDialog();
      void queryClient.invalidateQueries({ queryKey: ['tenant-config-overrides', tenantId] });
    },
  });

  const pushMutation = useMutation({
    mutationFn: () => {
      let overrides: Record<string, unknown> = {};
      try {
        overrides = JSON.parse(layers.pushOverridesJson || '{}') as Record<string, unknown>;
      } catch {
        throw new Error('Push-time overrides must be valid JSON');
      }
      return providerGatewayConfigService.pushEfemGatewayConfig(serialNumber, { overrides });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['efem-gateway-config-history', serialNumber] });
    },
  });

  const resendMutation = useMutation({
    mutationFn: (version: string) => providerGatewayConfigService.resendGatewayConfigMqtt(serialNumber, version),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['efem-gateway-config-history', serialNumber] });
    },
  });

  const exampleJson = useMemo(() => {
    const merged = registryTemplateQuery.data?.data?.merged;
    if (!merged) return '{}';
    return jsonStringify(mergedConfigToUnified(merged as Record<string, unknown>));
  }, [registryTemplateQuery.data]);

  const handleSaveOverride = (json: string): void => {
    if (layers.editingLayer === 2) {
      saveTenantOverridesMutation.mutate(json);
    } else if (layers.editingLayer === 3) {
      saveGatewayOverridesMutation.mutate(json);
    } else if (layers.editingLayer === 4) {
      layers.applyPushLayerOnly();
    }
  };

  const handlePreviewDeploy = async (): Promise<void> => {
    setDeployPreviewOpen(true);
    setDeployPreviewLoading(true);
    setDeployPreviewError(null);
    setDeployPreview(null);
    try {
      let overrides: Record<string, unknown> = {};
      try {
        overrides = JSON.parse(layers.pushOverridesJson || '{}') as Record<string, unknown>;
      } catch {
        throw new Error('Push-time overrides must be valid JSON');
      }
      const res = await providerGatewayConfigService.postGatewayConfigPreview(serialNumber, { overrides });
      setDeployPreview(res.data);
    } catch (err) {
      setDeployPreviewError(err instanceof Error ? err : new Error('Preview failed'));
    } finally {
      setDeployPreviewLoading(false);
    }
  };

  const handleViewVersionConfig = async (version: string): Promise<void> => {
    setViewingVersion(version);
    setIsLoadingVersionConfig(true);
    setVersionConfigError(null);
    setViewingConfig(null);
    try {
      const res = await providerGatewayConfigService.getGatewayConfigHistoryVersion(serialNumber, version);
      setViewingConfig(res.data);
    } catch (err) {
      setVersionConfigError(err instanceof Error ? err : new Error('Failed to load version config'));
    } finally {
      setIsLoadingVersionConfig(false);
    }
  };

  const history = historyQuery.data?.data?.items ?? [];
  const showResendForVersion = gatewayConfigResendEligibleVersion(history);
  const deployPreviewJson = deployPreview?.merged
    ? jsonStringify(mergedConfigToUnified(deployPreview.merged))
    : '{}';
  const viewingConfigJson = viewingConfig?.merged
    ? jsonStringify(mergedConfigToUnified(viewingConfig.merged))
    : '{}';
  const deployWarnings = useMemo(
    () => deployPreview?.warnings?.filter((w) => !w.toLowerCase().includes('profiles[]')),
    [deployPreview?.warnings],
  );
  const viewingWarnings = useMemo(
    () => viewingConfig?.warnings?.filter((w) => !w.toLowerCase().includes('profiles[]')),
    [viewingConfig?.warnings],
  );

  const layerRows = useMemo(
    () => [
      {
        layer: 1 as const,
        label: '1. Registry template',
        scope: 'All tenants (read-only)',
        persistenceLabel: 'Read-only',
        persistenceIcon: 'lock' as const,
        status: { label: 'Active', color: 'success' as const },
        action: 'view' as const,
        actionAriaLabel: 'View registry template',
      },
      {
        layer: 2 as const,
        label: '2. Tenant overrides',
        scope: 'All gateways for this tenant',
        persistenceLabel: 'Saved (tenant)',
        status: layers.tenantStatus,
        statusLoading: tenantOverridesQuery.isLoading,
        action: 'edit' as const,
        actionDisabled: !tenantId,
        actionAriaLabel: 'Edit tenant overrides',
      },
      {
        layer: 3 as const,
        label: '3. Gateway overrides',
        scope: 'This gateway only',
        persistenceLabel: 'Saved (gateway)',
        status: layers.gatewayStatus,
        statusLoading: gatewayOverridesQuery.isLoading,
        action: 'edit' as const,
        actionAriaLabel: 'Edit gateway overrides',
      },
      {
        layer: 4 as const,
        label: '4. Push-time overrides',
        scope: 'This push only (not saved)',
        persistenceLabel: 'Push only',
        persistenceIcon: 'push' as const,
        persistenceColor: 'warning' as const,
        status: layers.pushStatus,
        action: 'edit' as const,
        actionAriaLabel: 'Edit push-time overrides',
      },
    ],
    [
      layers.tenantStatus,
      layers.gatewayStatus,
      layers.pushStatus,
      tenantOverridesQuery.isLoading,
      gatewayOverridesQuery.isLoading,
      tenantId,
    ],
  );

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
        Gateway configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Layered templates, tenant and gateway overrides, and deployment preview.
      </Typography>

      {!tenantId && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Gateway is not assigned to a tenant. Allocate the device before pushing config.
        </Alert>
      )}
      {!hasAssignment && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {PRODUCT_NAMES.AXON} adapter not commissioned. Assign an adapter before pushing config.
        </Alert>
      )}
      {hasAssignment && registryTemplateQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load registry template from server:{' '}
          {registryTemplateQuery.error instanceof Error
            ? registryTemplateQuery.error.message
            : 'Unknown error'}
        </Alert>
      )}

      <GatewayConfigLayersTable rows={layerRows} onOpenLayer={layers.openLayerDialog} />

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="text.secondary">
          Push Commands
        </Typography>
        <GatewayConfigPushCommands
          hasPushOverrides={layers.pushStatus.hasPushOverrides}
          previewDisabled={!hasAssignment}
          pushDisabled={!tenantId || !hasAssignment}
          previewLoading={deployPreviewLoading}
          pushPending={pushMutation.isPending}
          onPreview={() => void handlePreviewDeploy()}
          onPush={() => pushMutation.mutate()}
          pushError={pushMutation.isError
            ? (pushMutation.error instanceof Error ? pushMutation.error.message : 'Push failed')
            : null}
          pushSuccess={pushMutation.isSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Pushed version {formatGatewayConfigVersion(pushMutation.data?.data?.configVersion)} — status {pushMutation.data?.data?.status}
            </Alert>
          ) : null}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <GatewayConfigPushHistoryTable
        history={history}
        isLoading={historyQuery.isLoading}
        isFetching={historyQuery.isFetching}
        onRefresh={() => void historyQuery.refetch()}
        showResendForVersion={showResendForVersion}
        onResend={(version) => resendMutation.mutate(version)}
        resendPending={resendMutation.isPending}
        showViewConfigColumn
        onViewConfig={(version) => void handleViewVersionConfig(version)}
      />

      <OverridesDialog
        open={layers.editingLayer !== null}
        onClose={layers.closeLayerDialog}
        layer={layers.editingLayer ?? 2}
        title={
          layers.editingLayer === 1
            ? 'Registry Template (Layer 1)'
            : layers.editingLayer === 2
              ? 'Tenant Overrides (Layer 2)'
              : layers.editingLayer === 3
                ? 'Gateway Overrides (Layer 3)'
                : 'Push-time Overrides (Layer 4)'
        }
        scopeDescription={
          layers.editingLayer === 1
            ? 'All tenants (read-only)'
            : layers.editingLayer === 2
              ? 'Applied to ALL gateways for this tenant'
              : layers.editingLayer === 3
                ? 'Applied to this gateway only, merged after tenant overrides'
                : 'Applied for this push only (not saved to S3)'
        }
        localJson={
          layers.editingLayer === 1
            ? exampleJson
            : layers.editingLayer === 2
              ? layers.tenantOverridesJson
              : layers.editingLayer === 3
                ? layers.gatewayOverridesJson
                : layers.pushOverridesJson
        }
        savedJson={
          layers.editingLayer === 1
            ? exampleJson
            : layers.editingLayer === 2
              ? layers.savedTenantOverridesJson
              : layers.editingLayer === 3
                ? layers.savedGatewayOverridesJson
                : layers.savedPushOverridesJson
        }
        exampleJson={exampleJson}
        hasAssignment={hasAssignment}
        onSave={handleSaveOverride}
        onChange={(val) => {
          if (layers.editingLayer === 2) layers.setTenantOverridesJson(val);
          if (layers.editingLayer === 3) layers.setGatewayOverridesJson(val);
          if (layers.editingLayer === 4) layers.setPushOverridesJson(val);
        }}
        isSaving={
          layers.editingLayer === 2
            ? saveTenantOverridesMutation.isPending
            : layers.editingLayer === 3
              ? saveGatewayOverridesMutation.isPending
              : false
        }
        saveError={
          layers.editingLayer === 2
            ? (saveTenantOverridesMutation.isError
              ? (saveTenantOverridesMutation.error instanceof Error
                ? saveTenantOverridesMutation.error.message
                : 'Save failed')
              : null)
            : layers.editingLayer === 3
              ? (saveGatewayOverridesMutation.isError
                ? (saveGatewayOverridesMutation.error instanceof Error
                  ? saveGatewayOverridesMutation.error.message
                  : 'Save failed')
                : null)
              : null
        }
        onLoadSaved={async () => {
          if (layers.editingLayer === 2) {
            const res = await tenantOverridesQuery.refetch();
            return jsonStringify(res.data?.data);
          }
          if (layers.editingLayer === 3) {
            const res = await gatewayOverridesQuery.refetch();
            return jsonStringify(res.data?.data);
          }
          return '{}';
        }}
      />

      <MergedConfigDialog
        open={deployPreviewOpen}
        onClose={() => {
          setDeployPreviewOpen(false);
          setDeployPreviewError(null);
        }}
        title="Deploy Preview"
        json={deployPreviewJson}
        isLoading={deployPreviewLoading}
        isError={!!deployPreviewError}
        error={deployPreviewError}
        warnings={deployWarnings}
        beforeJson={<EfemPreviewExtras preview={deployPreview} />}
      />

      <MergedConfigDialog
        open={viewingVersion !== null}
        onClose={() => {
          setViewingVersion(null);
          setViewingConfig(null);
          setVersionConfigError(null);
        }}
        title={viewingVersion ? `Merged Config View — ${formatGatewayConfigVersion(viewingVersion)}` : 'Merged Config View'}
        json={viewingConfigJson}
        isLoading={isLoadingVersionConfig}
        isError={!!versionConfigError}
        error={versionConfigError}
        warnings={viewingWarnings}
        beforeJson={<EfemPreviewExtras preview={viewingConfig} />}
      />
    </Box>
  );
}
